import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Zap, Store, ShieldCheck, CreditCard, Sparkles, ArrowRight, Ticket, Trophy, Truck, Smartphone, Gift, Lock } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Logo } from "@/components/common";
import DropCard from "@/components/DropCard";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1672759043182-dde5467f8f76?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const STEPS = [
  { icon: Zap, title: "Browse Drops", desc: "Explore live drops & free giveaways on graded cards." },
  { icon: Ticket, title: "Grab a Seat", desc: "Pick your numbered seats and check out securely." },
  { icon: Trophy, title: "Live Fair Draw", desc: "Watch the slot-reel draw — powered by RANDOM.ORG." },
  { icon: Truck, title: "Win & Ship", desc: "Winners get their card shipped, fully tracked." },
];

export default function LandingPage() {
  const { user } = useAuth();
  const [drops, setDrops] = useState([]);
  const [shop, setShop] = useState([]);
  const [heroImg, setHeroImg] = useState(HERO);
  const url = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    if (user) {
      api.get("/drops", { params: { status: "ACTIVE" } }).then(({ data }) => setDrops(data.slice(0, 4))).catch(() => {});
    }
    api.get("/shop").then(({ data }) => setShop(data.filter((i) => i.status === "available").slice(0, 4))).catch(() => {});
    api.get("/settings").then(({ data }) => {
      if (data.hero_image) { setHeroImg(fileUrl(data.hero_image)); return; }
      api.get("/drops").then(({ data }) => {
        const withPhoto = (data || []).find((d) => d.prize_photo);
        if (withPhoto) setHeroImg(fileUrl(withPhoto.prize_photo));
      }).catch(() => {});
    }).catch(() => {});
  }, [user]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 via-transparent to-obsidian" />
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-rise">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-gold/30 text-gold text-xs font-mono tracking-wider mb-6">
                <Sparkles className="w-3.5 h-3.5" /> LIVE CARD DROPS
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
                The vault for <span className="gold-text">Pokémon</span> card drops.
              </h1>
              <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
                A community built for Pokémon card collectors, traders, and enthusiasts — discover exciting card <span className="text-gold font-semibold">drops</span>, <span className="text-gold font-semibold">giveaways</span>, featured collections, and chances to add something special to your collection.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/drops" data-testid="cta-browse-drops" className="gold-gradient text-obsidian font-bold px-6 py-3 rounded-xl flex items-center gap-2 hover:brightness-110 transition-[filter]">
                  <Zap className="w-5 h-5" /> Browse Drops
                </Link>
                <Link to="/shop" data-testid="cta-shop" className="card-surface text-white font-bold px-6 py-3 rounded-xl flex items-center gap-2 hover:border-gold/40 transition-colors">
                  <Store className="w-5 h-5 text-gold" /> Shop
                </Link>
                {!user && (
                  <Link to="/signup" data-testid="cta-signup" className="text-gold font-bold px-6 py-3 rounded-xl flex items-center gap-2 hover:text-gold-bright transition-colors">
                    Sign up <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
            <div className="relative animate-rise" style={{ animationDelay: "120ms" }}>
              <div className="relative rounded-3xl overflow-hidden card-surface gold-glow-lg aspect-[4/5] max-w-md mx-auto">
                <img src={heroImg} alt="Featured Pokémon card drop" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl border border-gold/25 p-4 flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 beacon" />
                  <span className="text-sm text-white font-semibold">Live drops happening now</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white text-center">How it works</h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="card-surface rounded-2xl p-6 animate-rise" style={{ animationDelay: `${i * 70}ms` }} data-testid={`how-step-${i}`}>
              <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center text-obsidian mb-4"><s.icon className="w-6 h-6" /></div>
              <div className="font-mono text-xs text-gold/70 mb-1">STEP {i + 1}</div>
              <h3 className="font-display font-bold text-white text-lg">{s.title}</h3>
              <p className="text-slate-400 text-sm mt-1.5">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED DROPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Featured drops</h2>
            <p className="text-xs font-mono text-gold/70 mt-1 flex items-center gap-1.5"><Lock className="w-3 h-3" /> MEMBERS ONLY · SIGN IN TO VIEW & JOIN</p>
          </div>
          <Link to="/drops" className="text-gold text-sm font-semibold flex items-center gap-1" data-testid="see-all-drops">See all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {!user ? (
          <div className="card-surface rounded-2xl p-10 sm:p-14 text-center relative overflow-hidden" data-testid="drops-locked">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/10 blur-3xl" />
            <Lock className="w-12 h-12 mx-auto text-gold" />
            <h3 className="font-display text-xl sm:text-2xl font-bold text-white mt-4">Drops are members-only</h3>
            <p className="text-slate-400 mt-2 max-w-md mx-auto">Create a free account to see live drops & giveaways, grab seats, and join the community draws.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/signup" className="gold-gradient text-obsidian font-bold px-6 py-3 rounded-xl" data-testid="locked-signup">Sign up free</Link>
              <Link to="/login" className="card-surface text-white font-bold px-6 py-3 rounded-xl hover:border-gold/40" data-testid="locked-login">Log in</Link>
            </div>
          </div>
        ) : drops.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {drops.map((d, i) => <DropCard key={d.id} drop={d} index={i} />)}
          </div>
        ) : (
          <div className="card-surface rounded-2xl p-12 text-center text-slate-400">
            <Gift className="w-10 h-10 mx-auto text-gold/40 mb-3" />
            No active drops right now — check back soon for the next drop.
          </div>
        )}
      </section>

      {/* SHOP PREVIEW */}
      {shop.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">1-of-1 Shop</h2>
            <Link to="/shop" className="text-gold text-sm font-semibold flex items-center gap-1" data-testid="see-all-shop">Shop all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {shop.map((i) => (
              <Link key={i.id} to="/shop" className="card-surface rounded-2xl overflow-hidden hover:border-gold/40 transition-colors">
                <div className="aspect-square bg-vault">
                  {i.photo ? <img src={fileUrl(i.photo)} alt={i.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Store className="w-8 h-8" /></div>}
                </div>
                <div className="p-3">
                  <p className="text-white text-sm font-semibold line-clamp-1">{i.title}</p>
                  <p className="text-gold font-mono font-bold text-sm mt-1">${i.price.toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { icon: ShieldCheck, title: "Fair random draws", desc: "Every winner drawn with true randomness from RANDOM.ORG." },
            { icon: CreditCard, title: "Secure Stripe payments", desc: "Checkout handled by Stripe — we never see your card." },
            { icon: Truck, title: "Tracked, insured shipping", desc: "Winners & buyers get tracking on every order." },
          ].map((t) => (
            <div key={t.title} className="card-surface rounded-2xl p-6 flex gap-4">
              <t.icon className="w-8 h-8 text-gold shrink-0" />
              <div><h3 className="font-display font-bold text-white">{t.title}</h3><p className="text-slate-400 text-sm mt-1">{t.desc}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* INSTALL / QR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="card-surface rounded-3xl p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-gold/10 blur-3xl" />
          <div className="grid md:grid-cols-2 gap-8 items-center relative">
            <div>
              <div className="inline-flex items-center gap-2 text-gold text-xs font-mono tracking-wider mb-4"><Smartphone className="w-4 h-4" /> INSTALL THE APP</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Add PokeDrop to your Home Screen</h2>
              <p className="text-slate-300 mt-3 max-w-md">Scan the code with your phone, then tap <b className="text-gold">Add to Home Screen</b> — PokeDrop launches full-screen like a native app. No app store needed.</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-400">
                <li>📱 <b className="text-white">iPhone:</b> Tap Share → Add to Home Screen</li>
                <li>🤖 <b className="text-white">Android / Desktop:</b> Tap the install prompt or ⋮ menu → Install</li>
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-2xl gold-glow" data-testid="install-qr">
                <QRCodeSVG value={url} size={168} fgColor="#0a0b0e" bgColor="#ffffff" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gold/15 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/drops" className="hover:text-gold">Drops</Link>
            <Link to="/shop" className="hover:text-gold">Shop</Link>
            <Link to="/terms" className="hover:text-gold">Terms</Link>
            <Link to="/privacy" className="hover:text-gold">Privacy</Link>
          </div>
          <p className="text-xs text-slate-600 font-mono">© 2026 PokeDrop</p>
        </div>
      </footer>
    </div>
  );
}
